import React from "react"; // eslint-disable-line @typescript-eslint/no-unused-vars
import { Link } from "react-router-dom";
import publicationsHeroBg from "../../assets/BG-2.png";
import whitePaperPdf from "../../assets/WhitePaper-VerifyTT_Embedding_Trust_in_the_Digital_Economy_of_Trinidad_and_Tobago-iw 1.pdf";
import igovttLogo from "../../assets/igovtt.png";
import "./PublicationsPage.css";
import { Header } from "../PageTemplate/Header";

const documentDetails = [
	{
		label: "Category",
		value: "White Paper",
	},
	{
		label: "Published",
		value: "March 2026",
	},
	{
		label: "Attribution",
		value: "This white paper was prepared by iGovTT as part of its mandate to design, implement, and steward national digital infrastructure initiatives.",
	},
	{
		label: "Author",
		value: "Irwin Williams, Head of Software Development, iGovTT",
	},
];

const paperOutline = [
	{ title: "Executive Summary", targetId: "executive-summary" },
	{ title: "The Trust Maturity Gap", targetId: "trust-maturity-gap" },
	{
		title: "From Digitisation To Infrastructure: Evaluating The Options",
		targetId: "from-digitisation-to-infrastructure-evaluating-the-options",
	},
	{ title: "Trust As Infrastructure", targetId: "trust-as-infrastructure" },
	{
		title: "Architecture And Open Standards",
		targetId: "architecture-and-open-standards",
	},
	{
		title: "Policy Actions For National Adoption",
		targetId: "policy-actions-national-adoption",
	},
	{
		title: "Sustainability And Ecosystem Development",
		targetId: "sustainability-and-ecosystem-development",
	},
	{ title: "Governance And Safeguards", targetId: "governance-safeguards" },
	{ title: "Scaling Beyond Education", targetId: "scaling-beyond-education" },
	{ title: "Methodological Note", targetId: "methodological-note" },
	{
		title: "Conclusion: Trust As Public Utility",
		targetId: "conclusion-trust-as-public-utility",
	},
];

const methodologicalBulletHighlights = [
	"1. Publicly available national digital indicators,",
	"2. Documented institutional practices",
	"3. International DPI standards and governance frameworks",
	"4. Comparative institutional logic",
];

const emphasizedParagraphs = [
	"Policy considerations include:",
	"The evidence base draws on:",
];

const whitePaperSections = [
	{
		id: "executive-summary",
		title: "EXECUTIVE SUMMARY",
		paragraphs: [
			[
				"Trinidad and Tobago does not lack digital adoption. Internet penetration reached approximately",
				"84.7 percent of the population in early 20251. Mobile connections exceeded total population",
				"levels, with more than 2.0 million active cellular subscriptions—equivalent to roughly 135",
				"percent penetration2. These indicators reflect widespread smartphone access and sustained",
				"digital engagement across the population. Online banking, digital job applications, mobile",
				"authentication, and web-based service portals are routine components of everyday interaction.",
			].join(" "),
			[
				"The challenge is therefore not access to digital channels, but the absence of embedded digital trust.",
				"Credentials may be exchanged electronically, yet verification remains manual.",
				"Documents are digitised, but assurance is not.",
			].join(" "),
			[
				"VerifyTT establishes a national digital trust rail — a reusable Digital Public Infrastructure",
				"(DPI) building block that embeds cryptographic assurance directly into digital credentials. It eliminates",
				"the trade-off between speed and integrity by making trust instantaneous, portable, and",
				"machine-verifiable. Education is the pilot; trust infrastructure is the objective.",
				"VerifyTT represents Trinidad and Tobago’s first operational DPI component and establishes a",
				"scalable foundation for digital verification across sectors.",
			].join(" "),
		],
	},
	{
		id: "trust-maturity-gap",
		title: "THE TRUST MATURITY GAP",
		paragraphs: [
			[
				"In the absence of a national digital verification infrastructure, credential exchange commonly",
				"relies on scanned documents and direct institutional confirmation.",
			].join(" "),
			[
				"In tertiary credential workflows, verification may require direct institutional confirmation and",
				"introduce measurable administrative delay.",
			].join(" "),
			[
				"Verification often requires manual review or contact with issuing institutions, introducing",
				"institutional burden and slowing downstream decisions. Employers must either defer hiring",
				"decisions pending confirmation or accept unverifiable digital artifacts. When trust must be",
				"negotiated transaction by transaction, it generates recurring cost.",
			].join(" "),
		],
	},
	{
		id: "from-digitisation-to-infrastructure-evaluating-the-options",
		title: "FROM DIGITISATION TO INFRASTRUCTURE: EVALUATING THE OPTIONS",
		paragraphs: [
			"Digitising documents is not equivalent to embedding trust.",
			[
				"Scanning a paper certificate into a PDF reduces physical handling but does not eliminate the",
				"need for independent verification. A digital document remains dependent on institutional",
				"confirmation unless its authenticity can be verified computationally.",
			].join(" "),
			[
				"International digital government frameworks distinguish clearly between digitising documents",
				"and transforming verification models. The OECD (Organisation for Economic Co-operation and Development) has emphasised that true digital transformation",
				"requires moving from document-centric workflows toward data-centric and reusable service",
				"architectures3.",
			].join(" "),
			[
				"If Trinidad and Tobago seeks to modernise credential verification, there are three structurally",
				"distinct approaches.",
			].join(" "),
		],
		subSections: [
			{
				title: "Option 1: Enhanced Manual Digitisation",
				paragraphs: ["Institutions may improve existing workflows by:"],
				bullets: [
					"Providing downloadable PDF certificates",
					"Establishing centralised verification hotlines",
					"Offering email-based confirmation services",
					"Expanding registrar verification teams",
				],
				trailingParagraphs: [
					"This approach preserves institutional control and minimises structural change.",
					[
						"However, it does not eliminate human mediation. Verification remains dependent on manual",
						"review or direct institutional confirmation.",
					].join(" "),
					[
						"Administrative burden research demonstrates that compliance processes requiring repeated",
						"human validation generate measurable time and effort costs for both institutions and service",
						"users4. The OECD has further identified administrative burden as a driver of service delay and",
						"institutional inefficiency when processes rely on repetitive verification steps5.",
					].join(" "),
					[
						"Scaling such systems typically increases staffing requirements proportionally to transaction",
						"volume. The transaction cost is not removed; it is redistributed.",
					].join(" "),
					"Digitisation improves format. It does not alter the trust architecture.",
				],
			},
			{
				title: "Option 2: Proprietary Vendor-Based Verification Platforms",
				paragraphs: [
					[
						"A second approach involves contracting a commercial provider to operate a credential",
						"verification platform.",
					].join(" "),
					"This model can provide:",
				],
				bullets: [
					"Rapid deployment",
					"Managed infrastructure",
					"Commercially mature tooling",
				],
				trailingParagraphs: [
					[
						"Proprietary credential services are widely used internationally in higher education and",
						"workforce sectors.",
					].join(" "),
					[
						"However, international digital governance literature highlights several structural considerations",
						"associated with vendor-dependent digital identity and credential ecosystems:",
					].join(" "),
				],
				trailingBullets: [
					"Recurring licensing and subscription costs",
					"Long-term switching barriers",
					"Data portability constraints",
					"Limited interoperability across sectors",
					"Dependence on continued vendor viability",
				],
				closingParagraphs: [
					[
						"The World Bank’s Identification for Development (ID4D) program emphasises the importance of",
						"open standards, interoperability, and vendor neutrality in foundational digital systems to avoid",
						"lock-in and preserve sovereign flexibility6.",
					].join(" "),
					[
						"Similarly, the GSMA Digital Identity framework underscores the need for portability and",
						"ecosystem-wide interoperability to support cross-sector trust frameworks7.",
					].join(" "),
					[
						"A proprietary solution may resolve verification within a defined institutional boundary, but it",
						"does not automatically establish reusable national infrastructure.",
					].join(" "),
				],
			},
			{
				title: "Option 3: Public Digital Infrastructure (DPI)",
				paragraphs: [
					[
						"A third approach is to establish a standards-based, modular digital trust layer as public",
						"infrastructure.",
					].join(" "),
					[
						"Digital Public Infrastructure (DPI), as articulated in international frameworks including GovStack",
						"and the UNDP DPI Safeguards Framework, is characterised by:",
					].join(" "),
				],
				bullets: [
					"Open standards",
					"Modular architecture",
					"Separation of core functional components",
					"Interoperability by design",
					"Public-interest governance",
				],
				trailingParagraphs: [
					[
						"DPI enables credentials to be cryptographically signed and independently verifiable without",
						"requiring direct institutional mediation.",
					].join(" "),
					[
						"The World Bank and UNDP have both emphasised that foundational digital systems benefit from",
						"modular, open architectures that support cross-sector reuse and long-term scalability8.",
					].join(" "),
					[
						"Unlike enhanced manual processes or proprietary platforms, DPI establishes a reusable trust rail",
						"that multiple sectors can adopt without architectural reinvention.",
					].join(" "),
					"The policy choice is therefore not between digital and non-digital systems.",
					"It is between:",
				],
				trailingBullets: [
					"Digitising manual verification",
					"Outsourcing verification into a closed ecosystem",
					"Establishing open, reusable national trust infrastructure",
				],
				closingParagraphs: ["VerifyTT adopts the third approach."],
			},
		],
	},
	{
		id: "trust-as-infrastructure",
		title: "TRUST AS INFRASTRUCTURE",
		paragraphs: [
			[
				"Trust moves opportunity. VerifyTT treats trust as a public utility — modular, reusable, and",
				"sector-agnostic. While education is the initial use case, the infrastructure extends to",
				"professional licensing, healthcare credentials, financial onboarding, and civil registry extracts.",
			].join(" "),
		],
		subSections: [
			{
				title: "THE DIGITAL TRUST RAIL",
				paragraphs: [
					[
						"The digital trust rail enables verified information to move securely between issuers, holders, and",
						"verifiers without repeated human mediation. Verification becomes a cryptographic integrity",
						"check rather than a manual negotiation.",
					].join(" "),
				],
			},
		],
	},
	{
		id: "architecture-and-open-standards",
		title: "ARCHITECTURE AND OPEN STANDARDS",
		paragraphs: [
			[
				"Built on the open-source Inji Verifiable Credentials Stack and compliant with W3C standards,",
				"VerifyTT separates issuance, storage, and verification. This ensures interoperability, data",
				"portability, and protection against vendor lock-in.",
			].join(" "),
		],
		subSections: [
			{
				title: "ARCHITECTURAL MODEL",
				paragraphs: [
					[
						"VerifyTT operates on a three-role trust architecture consistent with global verifiable credential",
						"standards:",
					].join(" "),
				],
				bullets: [
					"Issuers generate cryptographically signed credentials using controlled signing keys.",
					"Holders store credentials in digital wallets or secure repositories under user control.",
					"Verifiers validate credentials independently using published verification keys and standardised protocols.",
				],
				trailingParagraphs: [
					[
						"This separation ensures that trust does not depend on real-time institutional mediation. Once",
						"issued, a credential can be verified independently without requiring the issuing institution to be",
						"contacted for confirmation.",
					].join(" "),
				],
			},
			{
				title: "CRYPTOGRAPHIC TRUST MODEL",
				paragraphs: [
					[
						"Each credential is digitally signed using asymmetric cryptography. The issuer controls a private",
						"signing key, while a corresponding public verification key is made available through a trusted",
						"registry mechanism. Verification confirms:",
					].join(" "),
				],
				bullets: [
					"The credential has not been altered (integrity check).",
					"The issuer is recognised and valid.",
					"The credential has not been revoked.",
				],
				trailingParagraphs: [
					[
						"This model converts trust from a human confirmation process into a computational validation",
						"process.",
					].join(" "),
				],
			},
			{
				title: "Modular Infrastructure Components",
				paragraphs: [
					"The infrastructure consists of distinct but interoperable components:",
				],
				bullets: [
					"Issuance Module – Generates credentials based on defined schemas.",
					"Credential Store (Wallet / Web Locker) – Enables user-controlled storage and presentation.",
					"Verification Module – Performs cryptographic and issuer validation checks.",
					"Issuer Registry / Key Management – Maintains trust anchors and public verification keys.",
				],
				trailingParagraphs: [
					[
						"Because these components are modular, additional issuers or sectors can be onboarded without",
						"redesigning the core trust mechanisms.",
					].join(" "),
				],
			},
			{
				title: "WHY THE INJI STACK?",
				paragraphs: [
					[
						"VerifyTT is built on the open-source Inji Verifiable Credentials Stack. This selection was guided",
						"by architectural and governance considerations rather than vendor preference.",
					].join(" "),
					[
						"Inji complies with W3C Verifiable Credential standards and supports cryptographic signing,",
						"revocation, and portable machine-readable formats. This ensures interoperability across",
						"systems and future compatibility with other digital identity and credential ecosystems.",
					].join(" "),
					[
						"As an open-source stack, Inji supports sovereign hosting, code transparency, and long-term",
						"flexibility. Foundational trust infrastructure benefits from auditability and the ability to evolve",
						"independently of proprietary licensing constraints.",
					].join(" "),
					[
						"Inji’s modular design separates issuance, storage, and verification functions. This enables",
						"asynchronous adoption across institutions and sectors without requiring architectural",
						"reinvention.",
					].join(" "),
					[
						"The selection therefore reflects a standards-aligned, modular, and vendor-neutral approach",
						"consistent with international Digital Public Infrastructure principles.",
					].join(" "),
				],
			},
		],
		tableTitle:
			"EVALUATION CRITERIA FOR VERIFIABLE CREDENTIAL STACK SELECTION",
		tableIntro: [
			[
				"The selection of the Inji Verifiable Credentials Stack was guided by defined architectural,",
				"governance, and sustainability criteria.",
			].join(" "),
		],
		table: {
			headers: [
				"Evaluation Criterion",
				"Rationale for National Trust Infrastructure",
				"Inji Alignment",
			],
			rows: [
				[
					"Standards Compliance",
					"Must comply with globally recognised VC standards (e.g., W3C Verifiable Credentials) to ensure interoperability and future compatibility.",
					"Fully compliant with W3C VC specifications and cryptographic signing standards.",
				],
				[
					"Open-Source Governance",
					"Foundational infrastructure benefits from code transparency, auditability, and avoidance of proprietary dependency.",
					"Open-source stack with publicly accessible codebase and community governance.",
				],
				[
					"Modular Architecture",
					"Separation of issuance, storage, and verification enables sector-wide reuse and asynchronous adoption.",
					"Native separation of issuance (Certify), wallet (Web/Mobile), and verification modules.",
				],
				[
					"Vendor Neutrality",
					"Infrastructure must not depend on a single commercial operator for long-term viability.",
					"Stack can be hosted sovereignly and integrated with multiple service providers",
				],
				[
					"Data Portability",
					"Credentials and schemas must remain exportable and usable outside a single ecosystem.",
					"Supports portable, machine-readable credential formats (e.g., JSON-LD).",
				],
				[
					"Interoperability Potential",
					"Must enable cross-sector and potential cross-border verification",
					"Standards-based architecture supports interoperable verification.",
				],
				[
					"Deployment Readiness",
					"Implementation should not require foundational re-engineering from scratch.",
					"Mature DPI-aligned stack supported under DaaS engagement.",
				],
				[
					"Scalability Across Sectors",
					"Architecture must support expansion beyond education without redesign.",
					"Designed as reusable trust layer across domains.",
				],
			],
		},
	},
	{
		id: "policy-actions-national-adoption",
		title: "POLICY ACTIONS FOR NATIONAL ADOPTION",
		paragraphs: [
			[
				"The architectural case for VerifyTT is strongest when matched by a clear policy pathway for",
				"adoption. If VerifyTT is to function as national trust infrastructure rather than a pilot",
				"implementation, policy action will be required across three areas: legal recognition, institutional",
				"adoption, and operational sustainability.",
			].join(" "),
		],
		subSections: [
			{
				title: "LEGAL AND REGULATORY RECOGNITION",
				paragraphs: [
					[
						"Trinidad and Tobago already possesses a partial legal foundation for digital records through the",
						"Electronic Transactions Act, 2011, which recognises electronic records, electronic signatures,",
						"and the legal validity of information retained in electronic form. Relevant provisions addressing",
						"the legal recognition of electronic records, retention of information, original form requirements,",
						"and admissibility of electronic evidence should be reviewed to determine whether",
						"cryptographically verifiable credentials can operate within existing statutory language.9",
					].join(" "),
					[
						"A complementary review should consider the Evidence Act (as amended 2021), which already",
						"provides mechanisms for establishing the authenticity of electronic records through certified",
						"digital evidence.10 In addition, the Data Protection Act should be examined in the context of",
						"selective disclosure and wallet-based credential presentation to ensure compliance with",
						"principles governing personal data processing and disclosure.11",
					].join(" "),
					[
						"The review should also extend to sectoral administrative rules where statutory instruments",
						"continue to require “certified copies,” “original documents,” or wet signatures in licensing,",
						"recruitment, or registry procedures. The objective is not to replace statutory authority but to",
						"allow verifiable credentials to operate alongside existing documentary requirements as legally",
						"recognised digital representations of issued records.",
					].join(" "),
					[
						"Comparable approaches have been adopted internationally. The European Union’s eIDAS 2.0",
						"framework extends existing electronic identification and trust service regulations to recognise",
						"digital identity wallets and verifiable credentials across public and private services.12 Similarly,",
						"the U.S. state of Utah has enacted legislation directing state agencies to pilot and recognise",
						"digital verifiable credentials as official government records.13",
					].join(" "),
				],
			},
			{
				title: "INSTITUTIONAL ADOPTION",
				paragraphs: [
					[
						"Digital trust infrastructure becomes valuable only when multiple institutions participate in the",
						"same verification ecosystem. Early adoption strategies should therefore focus on sectors where",
						"verification is already routine and administratively burdensome.",
					].join(" "),
					[
						"International public administration research has shown that repetitive verification processes",
						"create measurable administrative burden for both institutions and citizens.14 Consistent with the",
						"OECD’s “Once-Only Principle”, governments increasingly seek to reduce repeated document",
						"submission and redundant verification across services.",
					].join(" "),
					"For Trinidad and Tobago, this suggests a pragmatic adoption pathway:",
				],
				bullets: [
					"Establish measurable onboarding targets for the first 24 months, including at least two additional regulated sectors beyond education;",
					"Conduct structured outreach to employers, professional bodies, and financial institutions to expand verifier participation;",
					"Launch public communication emphasising the practical benefits of verifiable credentials, including faster verification, reduced fraud exposure, and improved administrative efficiency.",
				],
				trailingParagraphs: [
					[
						"International deployments have followed similar sector-focused strategies. In British Columbia,",
						"digital credentials have been introduced through partnerships between government and",
						"professional regulators, allowing lawyers to present verifiable credentials for secure court",
						"system access.",
					].join(" "),
					[
						"Early adoption should prioritise high-volume government workflows where credential",
						"verification already occurs frequently. Demonstrating reduced processing time and",
						"administrative handling in one such workflow can accelerate broader institutional adoption.",
					].join(" "),
				],
			},
		],
	},
	{
		id: "sustainability-and-ecosystem-development",
		title: "SUSTAINABILITY AND ECOSYSTEM DEVELOPMENT",
		paragraphs: [
			[
				"Long-term success requires VerifyTT to operate as durable infrastructure rather than a time-",
				"limited pilot. This requires clear institutional and financial arrangements.",
			].join(" "),
			"Policy considerations include:",
		],
		bullets: [
			"Defining a transparent operational funding and cost-recovery model for the trust infrastructure;",
			"Establishing a public–private collaboration framework allowing participation by professional bodies, financial institutions, and other credential issuers;",
			"Creating an international advisory mechanism to support standards alignment, capacity development, and ecosystem partnerships.",
		],
		closingParagraphs: [
			[
				"International initiatives such as GovStack and the World Bank’s Identification for Development",
				"(ID4D) program demonstrate the value of coordinated international advisory and technical",
				"networks in supporting national digital infrastructure development.",
			].join(" "),
			[
				"Taken together, these policy actions would enable VerifyTT to move beyond pilot",
				"implementation toward becoming a stable national capability for trusted digital verification.",
			].join(" "),
		],
	},
	{
		id: "governance-safeguards",
		title: "GOVERNANCE AND SAFEGUARDS",
		paragraphs: [
			[
				"VerifyTT operates under MPAAI strategic oversight, implemented by iGovTT, with CDPI advisory",
				"support and Deloitte under the DaaS framework. Safeguards include cryptographic signing,",
				"revocation capability, role-based access controls, and structured export readiness.",
			].join(" "),
			[
				"Institutional alignment, verifier adoption, sustainability planning, and legal recognition are",
				"acknowledged as structured implementation considerations. These are managed realities, not",
				"structural weaknesses.",
			].join(" "),
		],
	},
	{
		id: "scaling-beyond-education",
		title: "SCALING BEYOND EDUCATION",
		paragraphs: [
			[
				"The underlying credential infrastructure — including cryptographic signing mechanisms,",
				"verification protocols, wallet integration, and issuer registry services — remains consistent",
				"across use cases. Expansion into additional sectors does not require rebuilding these",
				"foundational components. Instead, new credential types are introduced through the definition",
				"of sector-specific schemas that operate within the same standards-based framework.",
			].join(" "),
			[
				"In practical terms, this means that onboarding a professional licensing body or a healthcare",
				"authority requires governance alignment and schema configuration, not architectural redesign.",
				"The verification logic, cryptographic validation process, and presentation mechanisms remain",
				"unchanged.",
			].join(" "),
		],
		subSections: [
			{
				title: "FIVE YEARS ON: INSTITUTIONALISATION OF THE TRUST RAIL",
				paragraphs: [
					[
						"Within five years, the measure of VerifyTT’s success will not be the volume of credentials issued,",
						"but the normalisation of embedded digital verification across sectors.",
					].join(" "),
					[
						"The foundational components — cryptographic signing, verification protocols, wallet",
						"integration, and issuer registry services — will remain unchanged. What evolves is sectoral",
						"participation.",
					].join(" "),
				],
			},
			{
				title: "EDUCATION AS DEFAULT DIGITAL ISSUANCE",
				paragraphs: [
					[
						"Tertiary institutions will issue digital credentials as a routine complement to physical artifacts.",
						"Verification will occur through computational validation rather than direct institutional",
						"confirmation. Manual confirmation will shift from standard practice to exception handling.",
					].join(" "),
				],
			},
			{
				title: "REGULATED SECTOR ONBOARDING",
				paragraphs: [
					[
						"At least one additional regulated domain — such as professional licensing or healthcare",
						"credentialing — will operate on the same trust infrastructure. Onboarding will require schema",
						"definition and governance alignment, not architectural redesign.",
					].join(" "),
					[
						"Issuers will retain authority over issuance and revocation. The infrastructure will standardise",
						"verification, not alter regulatory control.",
					].join(" "),
				],
			},
			{
				title: "VERIFICATION EMBEDDED IN WORKFLOWS",
				paragraphs: [
					[
						"Verification will increasingly be integrated into institutional systems — recruitment platforms,",
						"licensing checks, and compliance processes — rather than conducted through ad hoc document",
						"review.",
					].join(" "),
					"Trust will become an automated validation layer within existing workflows.",
				],
			},
			{
				title: "GOVERNANCE MATURATION",
				paragraphs: [
					[
						"Issuer accreditation processes, schema approval mechanisms, and revocation governance will",
						"be formalised. Operational oversight will shift from project implementation to infrastructure",
						"stewardship.",
					].join(" "),
					"The trust rail will function as a stable public capability rather than a pilot initiative.",
				],
			},
		],
	},
	{
		id: "methodological-note",
		title: "METHODOLOGICAL NOTE",
		paragraphs: [
			[
				"This white paper is based on a structured analysis of Trinidad and Tobago’s digital ecosystem,",
				"documented institutional workflows within the tertiary education sector, and internationally",
				"recognised Digital Public Infrastructure (DPI) design principles.",
			].join(" "),
			"The evidence base draws on:",
		],
		bullets: [
			[
				"1. Publicly available national digital indicators, including internet penetration, mobile",
				"connectivity, and broadband adoption data published by internationally recognised",
				"sources such as the International Telecommunication Union (ITU), the World Bank, and",
				"global digital monitoring reports.",
			].join(" "),
			[
				"2. Documented institutional practices observed within the tertiary education",
				"credentialing environment during the design and implementation of the VerifyTT pilot.",
				"These observations include paper-based issuance processes, manual verification",
				"workflows, and administrative handling procedures.",
			].join(" "),
			[
				"3. International DPI standards and governance frameworks, including W3C Verifiable",
				"Credential specifications and open-source modular credentialing architectures, which",
				"inform the technical and architectural analysis presented.",
			].join(" "),
			[
				"4. Comparative institutional logic, drawing on established public administration and",
				"transaction-cost principles to assess the systemic impact of manual verification",
				"processes.",
			].join(" "),
		],
		closingParagraphs: [
			[
				"Where quantitative national statistics are available, they are cited. Where direct institutional",
				"workflows are described, they reflect documented operational practice within the pilot scope.",
				"Analytical conclusions are derived from the structural implications of these practices rather than",
				"from generalised national surveys.",
			].join(" "),
			[
				"This paper does not claim to provide exhaustive sector-wide statistical measurement. Rather, it",
				"presents a policy and infrastructure analysis grounded in documented practice and",
				"internationally recognised standards to articulate the rationale for establishing a national digital",
				"trust infrastructure.",
			].join(" "),
		],
	},
	{
		id: "conclusion-trust-as-public-utility",
		title: "CONCLUSION: TRUST AS PUBLIC UTILITY",
		paragraphs: [
			[
				"VerifyTT marks a shift from manual verification to embedded assurance. By embedding trust",
				"directly within its digital economy, Trinidad and Tobago transitions from negotiating trust to",
				"sustaining it as infrastructure.",
			].join(" "),
		],
	},
];

const FeaturedWhitePaperPage = () => {
	
	return (
		<div
			style={{ margin: 0, padding: 0, background: "#F8FAFC", zoom: 0.97 }}
		>
			<div
				style={{
					width: "100%",
					height: "100%",
					background: "#F8FAFC",
					overflow: "visible",
					flexDirection: "column",
					justifyContent: "flex-start",
					alignItems: "flex-start",
					display: "inline-flex",
				}}
			>
				<Header />

				<section
					className="w-full flex justify-center px-4 pt-6 pb-10 sm:px-6 lg:px-8"
					aria-label="Featured white paper hero"
				>
					<div className="w-full max-w-[1204px]">
						<nav
							aria-label="Breadcrumb"
							className="mb-5 flex flex-wrap items-center gap-2 text-[14px] font-normal"
						>
							<Link
								to="/publication"
								className="text-[#333333] no-underline transition-colors hover:text-[#116DFD]"
							>
								Publication
							</Link>
							<span aria-hidden="true" className="text-[#64748b]">
								›
							</span>
							<span className="max-w-[860px] truncate text-[#116DFD]">
								Verifytt: Embedding Trust In The Digital Economy
								Of Trinidad And Tobago
							</span>
						</nav>

						<div
							className="relative overflow-hidden rounded-[26px] border border-white/12 bg-[#0a1020] bg-cover bg-center bg-no-repeat shadow-[0_18px_40px_rgba(15,23,42,0.18)]"
							style={{
								backgroundImage: `url(${publicationsHeroBg})`,
							}}
						>
							<div
								className="absolute -left-10 -top-14 h-[120px] w-[120px] rounded-full bg-white/8 blur-[2px]"
								aria-hidden="true"
							/>
							<div
								className="absolute -bottom-28 -right-20 h-[260px] w-[260px] rounded-full bg-white/6"
								aria-hidden="true"
							/>
							<div
								className="absolute -bottom-16 right-[-20px] h-[160px] w-[160px] rounded-full bg-white/6"
								aria-hidden="true"
							/>

							<div className="relative flex min-h-[386px] flex-col justify-between px-10 py-14">
								<div className="max-w-[760px]">
									<div className="text-[16px] font-semibold uppercase tracking-[0.06em] text-[#2c7df7]">
										Latest White Paper
									</div>
									<h1
										className="mt-6 mb-0 max-w-[760px] text-[30px] font-bold 
                   text-white"
									>
										Verifytt: Embedding Trust In The Digital
										Economy Of Trinidad And Tobago
									</h1>
									<p className="mt-8 mb-0 text-[16px] leading-7 text-white">
										<span className="font-bold text-white">
											Lead Author:
										</span>{" "}
										<span className="font-normal">
											Irwin Williams, Head of Software
											Development, iGovTT
										</span>
									</p>
								</div>

								<div className="mt-14 flex flex-wrap gap-4">
									<a
										href={whitePaperPdf}
										download="VerifyTT-Embedding-Trust-White-Paper.pdf"
										className="inline-flex min-h-[50px] items-center justify-center rounded-[10px] border border-[#d1d5db] bg-[#f8fafc] px-7 py-3 text-[14px] font-semibold text-[#3b4252] no-underline shadow-[0_6px_14px_rgba(15,23,42,0.1)] transition-colors"
									>
										Download PDF
									</a>
									{/* <button
										type="button"
										className="inline-flex min-h-[50px] items-center justify-center ml-3 rounded-[10px] border border-[#FFFFFF4D] bg-white/15 px-7 py-3 text-[14px] font-semibold text-white transition-colors"
									>
										Share White Paper
									</button> */}
								</div>
							</div>
						</div>
					</div>
				</section>

				<section className="w-full flex justify-center px-4 pb-16 sm:px-6 lg:px-8">
					<div className="grid w-full max-w-[1204px] grid-cols-1 gap-6 xl:grid-cols-[260px_minmax(0,1fr)] xl:gap-8">
						<aside className="flex flex-col gap-6 xl:pt-2">
							<div className="rounded-[24px] border border-[#E7ECF3] bg-white p-6 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
								<p className="m-0 text-[16px] font-bold uppercase text-[#116DFD]">
									Document Details
								</p>
								<div className="mt-6 flex flex-col gap-3">
									{documentDetails.map((item) => (
										<div
											key={item.label}
											className="flex flex-col gap-0"
										>
											<p className="m-0 text-[16px] font-semibold leading-6 text-[#161D2E]">
												{item.label}
											</p>
											<p className="m-0 text-[14px] font-light leading-6 text-[#333333]">
												{item.value}
											</p>
										</div>
									))}
								</div>
							</div>

							<div className="rounded-[24px] border border-[#E7ECF3] bg-white p-6 shadow-[0_10px_24px_rgba(15,23,42,0.04)] xl:sticky xl:top-24 xl:self-start">
								<p className="m-0 text-[16px] font-bold uppercase text-[#116DFD]">
									In This Paper
								</p>
								<div className="mt-6 flex flex-col gap-6">
									{paperOutline.map((item) => (
										<a
											key={`${item.title}-${item.targetId}`}
											href={`#${item.targetId}`}
											className="m-0 text-[14px] font-normal uppercase text-[#333333] no-underline transition-colors hover:text-[#116DFD]"
										>
											{item.title}
										</a>
									))}
								</div>
							</div>
						</aside>

						<article className="rounded-[18px] border border-[#e5e9f2] bg-white p-6 shadow-[0_10px_24px_rgba(15,23,42,0.04)] sm:p-8 lg:p-10">
							<div className="flex flex-col gap-14">
								{whitePaperSections.map((section) => (
									<section
										key={section.id}
										id={section.id}
										className="scroll-mt-24"
									>
										<h2 className="m-0 text-[16px] tracking-wide font-semibold leading-7 text-[#116DFD]">
											{section.title}
										</h2>
										<div className="mt-4 flex flex-col gap-3 text-[16px] font-normal leading-7 text-[#333333]">
											{section.paragraphs?.map(
												(paragraph) => (
													<p
														key={paragraph}
														className={`m-0 ${emphasizedParagraphs.includes(paragraph) ? "font-bold text-[#0f172a]" : ""}`}
													>
														{paragraph}
													</p>
												),
											)}

											{section.bullets ? (
												<ul className="m-0 list-disc pl-6 text-[16px] font-normal leading-6 text-[#334155] marker:text-[#334155]">
													{section.bullets.map(
														(bullet) => (
															<li
																key={bullet}
																className="mb-1 text-[16px] font-normal leading-6"
															>
																{methodologicalBulletHighlights.find(
																	(
																		highlight,
																	) =>
																		bullet.startsWith(
																			highlight,
																		),
																) ? (
																	<>
																		<span className="font-bold text-[#0f172a]">
																			{methodologicalBulletHighlights.find(
																				(
																					highlight,
																				) =>
																					bullet.startsWith(
																						highlight,
																					),
																			)}
																		</span>
																		{bullet.slice(
																			methodologicalBulletHighlights.find(
																				(
																					highlight,
																				) =>
																					bullet.startsWith(
																						highlight,
																					),
																			)
																				.length,
																		)}
																	</>
																) : (
																	bullet
																)}
															</li>
														),
													)}
												</ul>
											) : null}

											{section.closingParagraphs?.map(
												(paragraph) => (
													<p
														key={paragraph}
														className="m-0"
													>
														{paragraph}
													</p>
												),
											)}

											{section.subSections?.map(
												(subSection) => (
													<div
														key={subSection.title}
														className="flex flex-col gap-4 pt-6 first:pt-3"
													>
														<h3 className="m-0 text-[16px] font-medium leading-7 text-[#0f172a]">
															{subSection.title}
														</h3>

														{subSection.paragraphs?.map(
															(paragraph) => (
																<p
																	key={
																		paragraph
																	}
																	className="m-0"
																>
																	{paragraph}
																</p>
															),
														)}

														{subSection.bullets ? (
															<ul className="m-0 list-disc pl-6 text-[16px] font-normal leading-6 text-[#334155] marker:text-[#334155]">
																{subSection.bullets.map(
																	(
																		bullet,
																	) => (
																		<li
																			key={
																				bullet
																			}
																			className="mb-1 text-[16px] font-normal leading-6"
																		>
																			{
																				bullet
																			}
																		</li>
																	),
																)}
															</ul>
														) : null}

														{subSection.trailingParagraphs?.map(
															(paragraph) => (
																<p
																	key={
																		paragraph
																	}
																	className="m-0"
																>
																	{paragraph}
																</p>
															),
														)}

														{subSection.trailingBullets ? (
															<ul className="m-0 list-disc pl-6 text-[16px] font-normal leading-6 text-[#334155] marker:text-[#334155]">
																{subSection.trailingBullets.map(
																	(
																		bullet,
																	) => (
																		<li
																			key={
																				bullet
																			}
																			className="mb-1 text-[16px] font-normal leading-6"
																		>
																			{
																				bullet
																			}
																		</li>
																	),
																)}
															</ul>
														) : null}

														{subSection.closingParagraphs?.map(
															(paragraph) => (
																<p
																	key={
																		paragraph
																	}
																	className="m-0"
																>
																	{paragraph}
																</p>
															),
														)}
													</div>
												),
											)}

											{section.tableTitle ? (
												<h3 className="m-0 pt-6 text-[16px] font-medium leading-7 text-[#0f172a]">
													{section.tableTitle}
												</h3>
											) : null}

											{section.tableIntro?.map(
												(paragraph) => (
													<p
														key={paragraph}
														className="mb-5"
													>
														{paragraph}
													</p>
												),
											)}

											{section.table ? (
												<div className="overflow-x-auto -mx-2 px-2">
												<div className="overflow-hidden rounded-[12px] border border-[#dce3ef] min-w-[600px]">
													<table className="w-full border-collapse text-left text-[13px] leading-6 text-[#334155]">
														<thead className="bg-[#111827] text-white">
															<tr>
																{section.table.headers.map(
																	(
																		header,
																	) => (
																		<th
																			key={
																				header
																			}
																			className="px-4 py-3 text-[12px] font-semibold uppercase tracking-[0.06em]"
																		>
																			{
																				header
																			}
																		</th>
																	),
																)}
															</tr>
														</thead>
														<tbody>
															{section.table.rows.map(
																(row) => (
																	<tr
																		key={
																			row[0]
																		}
																		className="border-t border-[#e2e8f0] bg-white align-top"
																	>
																		{row.map(
																			(
																				cell,
																				index,
																			) => (
																				<td
																					key={`${row[0]}-${index}`}
																					className={`px-4 py-3 ${index === 0 ? "font-bold text-[#0f172a]" : ""}`}
																				>
																					{
																						cell
																					}
																				</td>
																			),
																		)}
																	</tr>
																),
															)}
														</tbody>
													</table>
												</div>
												</div>
											) : null}
										</div>
									</section>
								))}
							</div>
						</article>
					</div>
				</section>

				<div
					className="vt-footer"
					role="contentinfo"
					aria-label="Footer"
				>
					<div className="vt-footer-inner">
						<div className="vt-footer-top">
							<div
								className="vt-footer-top-row"
								style={{
									alignItems: "center",
									gap: "32px",
									flexWrap: "wrap",
									justifyContent: "space-between",
								}}
							>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "32px",
										flexWrap: "wrap",
									}}
								>
									<div className="vt-footer-brand">
										<div className="vt-footer-logo-row">
											<div
												className="vt-footer-logo-wrapper"
												aria-label="VerifyTT logo"
											>
												<img
													alt="VerifyTT logo"
													src="https://t-t-prod-c3atgyatcbgvfdhc.a02.azurefd.net/images/logo.svg"
												/>
											</div>
										</div>
										<div className="vt-footer-tagline">
											Trusted Credentials. Verified.
										</div>
									</div>

									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "14px",
											flexWrap: "wrap",
										}}
									>
										<img
											src={igovttLogo}
											alt="iGovTT logo"
											style={{
												height: "76px",
												width: "auto",
												objectFit: "contain",
											}}
										/>
									</div>
								</div>

								<div className="vt-footer-ministry">
									An Initiative of the Ministry of Public
									Administration and Artificial Intelligence
									and The National Information and
									Communication Technology Company Limited.
								</div>
							</div>
						</div>

						<div className="vt-footer-divider-wrapper">
							<div className="vt-footer-divider"></div>
						</div>

						<div className="vt-footer-bottom">
							<div className="vt-footer-copy">
								©2025 VerifyTT · All rights reserved.
							</div>
							<div
								className="vt-footer-links"
								aria-label="Footer links"
							>
								<div className="vt-footer-link-item">
									<div className="vt-footer-link-inner">
										<div className="vt-footer-link-text-wrapper">
											<div className="vt-footer-link-text">
												Term of use
											</div>
										</div>
									</div>
								</div>
								<div className="vt-footer-link-item">
									<div className="vt-footer-link-inner">
										<div className="vt-footer-link-text-wrapper">
											<div className="vt-footer-link-text">
												<a
													aria-label="Privacy policy — opens in a new tab"
													href="https://dev-verifytt.gov.tt/privacy-policy.html"
													target="_blank"
													rel="noreferrer"
												>
													Privacy policy
												</a>
											</div>
										</div>
									</div>
								</div>
								<div className="vt-footer-link-item">
									<div className="vt-footer-link-inner">
										<div className="vt-footer-link-text-wrapper">
											<div className="vt-footer-link-text">
												Help / Support
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default FeaturedWhitePaperPage;
